/**
 * NIGHTBOUND - shelter panels.
 *
 * Small, presentational panels that render the authoritative creature view.
 * Nothing here computes game state: every number is read straight off the view
 * the server returned.
 */

import { NEED_THRESHOLDS, DAILY_CAPS } from '../../shared/constants'
import { MOODS } from '../../shared/mood'
import { ACTIVITY_GLYPHS, ACTIVITY_LABELS } from '../../shared/activity'
import { SPECIES } from '../../shared/species'
import { TRAINING } from '../../shared/training'
import type { CreatureView } from '../../shared/types'
import type { CreatureNeeds } from '../../shared/types'
import { Chip, Meter, Panel, PanelHeading, cx } from './ui'

// ---------------------------------------------------------------------------
// Needs + health
// ---------------------------------------------------------------------------

const NEED_ROWS: Array<{
  key: keyof CreatureNeeds
  label: string
  icon: string
  color: string
  lowAt: number
}> = [
  { key: 'hunger', label: 'Fed', icon: '🍖', color: '#c9a46a', lowAt: NEED_THRESHOLDS.hunger },
  { key: 'energy', label: 'Energy', icon: '⚡', color: '#8f9bb3', lowAt: NEED_THRESHOLDS.energy },
  { key: 'hygiene', label: 'Clean', icon: '≈', color: '#83914f', lowAt: NEED_THRESHOLDS.hygiene },
  { key: 'bond', label: 'Bond', icon: '♥', color: '#a8452f', lowAt: NEED_THRESHOLDS.bond },
  { key: 'sanity', label: 'Sanity', icon: '☾', color: '#a7b2c6', lowAt: NEED_THRESHOLDS.sanity },
  { key: 'fitness', label: 'Fitness', icon: '⚒', color: '#b8b1a4', lowAt: 20 },
]

export function NeedsPanel({
  needs,
  hp,
  maxHp,
  className,
}: {
  needs: CreatureNeeds
  hp: number
  maxHp: number
  className?: string
}) {
  const hpRatio = maxHp > 0 ? hp / maxHp : 1
  return (
    <Panel className={cx('p-3', className)}>
      <div className="space-y-3">
        <Meter
          label="Health"
          icon="✚"
          value={hp}
          max={maxHp}
          color={hpRatio <= 0.4 ? '#a8452f' : '#7a2f22'}
          lowAt={maxHp * 0.35}
        />
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2">
          {NEED_ROWS.map((row) => (
            <Meter
              key={row.key}
              label={row.label}
              icon={row.icon}
              value={needs[row.key]}
              color={row.color}
              lowAt={row.lowAt}
              compact
            />
          ))}
        </div>
      </div>
    </Panel>
  )
}

// ---------------------------------------------------------------------------
// Mood / condition strip
// ---------------------------------------------------------------------------

export function ConditionStrip({ creature }: { creature: CreatureView }) {
  const { visual } = creature
  const condition = visual.condition
  const mood = MOODS[visual.mood]

  const flags: Array<{ label: string; tone: 'warn' | 'bad' }> = []
  if (condition.injured) flags.push({ label: 'Injured', tone: 'bad' })
  if (condition.hungry) flags.push({ label: 'Hungry', tone: 'warn' })
  if (condition.dirty) flags.push({ label: 'Filthy', tone: 'warn' })
  if (condition.exhausted) flags.push({ label: 'Exhausted', tone: 'warn' })
  if (condition.disturbed) flags.push({ label: 'Disturbed', tone: 'bad' })

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <Chip tone="neutral">
        <span aria-hidden>{ACTIVITY_GLYPHS[visual.activity]}</span>
        {ACTIVITY_LABELS[visual.activity]}
      </Chip>
      <Chip tone="neutral">{mood.label}</Chip>
      {visual.sanityBand !== 'STEADY' ? (
        <Chip tone="bad">{visual.sanityBand.toLowerCase()}</Chip>
      ) : null}
      {flags.map((flag) => (
        <Chip key={flag.label} tone={flag.tone}>
          {flag.label}
        </Chip>
      ))}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Combat stats + XP
// ---------------------------------------------------------------------------

export function StatPanel({ creature }: { creature: CreatureView }) {
  const stats: Array<{ label: string; value: number }> = [
    { label: 'Health', value: Math.round(creature.stats.hp) },
    { label: 'Attack', value: creature.stats.attack },
    { label: 'Defence', value: creature.stats.defence },
    { label: 'Speed', value: creature.stats.speed },
  ]

  return (
    <Panel className="overflow-hidden">
      <PanelHeading right={<span className="nb-numeric text-[11px] text-ash-400">Lv {creature.level}</span>}>
        Vitals
      </PanelHeading>
      <div className="p-3">
        <div className="grid grid-cols-4 gap-2">
          {stats.map((stat) => (
            <div
              key={stat.label}
              className="rounded-[3px] border border-ash-700/40 bg-night-950/50 px-2 py-2 text-center"
            >
              <div className="nb-numeric text-base text-bone-200">{stat.value}</div>
              <div className="nb-label mt-0.5 text-[8px]">{stat.label}</div>
            </div>
          ))}
        </div>
        <div className="mt-3 space-y-1">
          <div className="flex items-center justify-between">
            <span className="nb-label text-[9px]">Experience</span>
            <span className="nb-numeric text-[10px] text-ash-300">
              {creature.level >= 60
                ? 'MAX'
                : `${creature.xp} / ${Math.round(creature.xpToNext)}`}
            </span>
          </div>
          <div className="h-1.5 overflow-hidden rounded-full border border-ash-700/40 bg-night-950/80">
            <div
              className="h-full rounded-full bg-gradient-to-r from-ember-600 to-ember-400"
              style={{ width: `${Math.round(creature.xpProgress * 100)}%` }}
            />
          </div>
        </div>
      </div>
    </Panel>
  )
}

// ---------------------------------------------------------------------------
// Training
// ---------------------------------------------------------------------------

export function TrainingPanel({ creature }: { creature: CreatureView }) {
  const rows = [...creature.training].sort(
    (a, b) => a.discipline.localeCompare(b.discipline),
  )
  return (
    <Panel className="overflow-hidden">
      <PanelHeading>Training</PanelHeading>
      <div className="space-y-3 p-3">
        {rows.length === 0 ? (
          <p className="text-xs text-ash-400">No disciplines recorded yet.</p>
        ) : (
          rows.map((row) => {
            const definition = TRAINING[row.discipline]
            return (
              <div key={row.discipline} className="space-y-1">
                <div className="flex items-center justify-between gap-2">
                  <span className="nb-label flex items-center gap-1.5 text-[10px]">
                    <span aria-hidden>{definition.glyph}</span>
                    {definition.name}
                  </span>
                  <span className="nb-numeric text-[10px] text-ash-300">
                    Lv {row.level} · {Math.round(row.progress * 100)}%
                  </span>
                </div>
                <div className="h-1.5 overflow-hidden rounded-full border border-ash-700/40 bg-night-950/80">
                  <div
                    className="h-full rounded-full transition-[width] duration-500"
                    style={{ width: `${Math.round(row.progress * 100)}%`, background: definition.accent }}
                  />
                </div>
                <p className="nb-label text-[8px] text-ash-500">
                  raises {definition.statLabel}
                </p>
              </div>
            )
          })
        )}
      </div>
    </Panel>
  )
}

// ---------------------------------------------------------------------------
// Abilities
// ---------------------------------------------------------------------------

export function AbilityPanel({ creature }: { creature: CreatureView }) {
  return (
    <Panel className="overflow-hidden">
      <PanelHeading right={<span className="nb-label text-[8px]">Combat only</span>}>
        Abilities
      </PanelHeading>
      <div className="divide-y divide-ash-700/30">
        {creature.abilities.map((ability) => (
          <div key={ability.key} className="flex items-start justify-between gap-3 px-3 py-2">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-sm text-bone-200">{ability.name}</span>
                {ability.equipped ? <Chip tone="good">Equipped</Chip> : null}
                {ability.source !== 'STARTING' ? (
                  <Chip tone="warn">{ability.source.toLowerCase()}</Chip>
                ) : null}
              </div>
              <p className="mt-0.5 text-[11px] leading-snug text-ash-400">{ability.description}</p>
            </div>
            <div className="shrink-0 text-right">
              <div className="nb-numeric text-sm text-bone-300">{ability.power}</div>
              <div className="nb-label text-[8px]">power</div>
            </div>
          </div>
        ))}
        {creature.abilities.length === 0 ? (
          <p className="px-3 py-3 text-xs text-ash-400">No abilities.</p>
        ) : null}
      </div>
    </Panel>
  )
}

// ---------------------------------------------------------------------------
// Daily limits (anti-farm transparency)
// ---------------------------------------------------------------------------

export function DailyPanel({ creature }: { creature: CreatureView }) {
  const { daily } = creature
  const rows = [
    { label: 'Bond gained', used: daily.bondGained, budget: DAILY_CAPS.bondGain },
    { label: 'Care XP', used: daily.xpGained, budget: DAILY_CAPS.careXp },
    { label: 'Training', used: daily.trainingSessions, budget: daily.trainingSessionBudget },
    { label: 'Affection', used: daily.petInteractions, budget: DAILY_CAPS.petInteractions },
  ]
  return (
    <Panel className="overflow-hidden">
      <PanelHeading right={<span className="nb-label text-[8px]">today</span>}>
        Care limits
      </PanelHeading>
      <div className="grid grid-cols-2 gap-x-4 gap-y-2 p-3">
        {rows.map((row) => {
          const full = row.used >= row.budget
          return (
            <div key={row.label} className="space-y-1">
              <div className="flex items-center justify-between gap-2">
                <span className="nb-label text-[9px]">{row.label}</span>
                <span className={cx('nb-numeric text-[10px]', full ? 'text-rot-500' : 'text-ash-300')}>
                  {Math.round(row.used)}/{row.budget}
                </span>
              </div>
              <div className="h-1 overflow-hidden rounded-full bg-night-950/80">
                <div
                  className={cx('h-full rounded-full', full ? 'bg-rot-600' : 'bg-ash-500')}
                  style={{ width: `${Math.min(100, (row.used / row.budget) * 100)}%` }}
                />
              </div>
            </div>
          )
        })}
      </div>
      <p className="border-t border-ash-700/30 px-3 py-2 text-[10px] leading-snug text-ash-500">
        Care past these limits still helps your creature, but it stops earning rewards. Levels come
        from many small days, not one long one.
      </p>
    </Panel>
  )
}

// ---------------------------------------------------------------------------
// Species / identity summary
// ---------------------------------------------------------------------------

export function IdentityPanel({
  creature,
  right,
}: {
  creature: CreatureView
  right?: React.ReactNode
}) {
  const species = SPECIES[creature.speciesId]
  return (
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <h1 className="truncate font-display text-2xl leading-tight text-bone-100">{creature.name}</h1>
        <p className="mt-0.5 text-[11px] uppercase tracking-[0.18em] text-ash-400">
          {species.name} · {species.role}
        </p>
        <p className="mt-1 text-[11px] text-ash-500">
          Day {creature.daysTogether} together · {Math.floor(creature.ageHours)}h old
        </p>
      </div>
      {right}
    </div>
  )
}
